import { useState } from 'react'
import { useCouncilMemos, useCouncilDirectives } from '../hooks/useApi'

const sessionColors: Record<string, string> = {
  am: 'badge-blue',
  pm: 'badge-purple',
}

function renderMarkdown(content: string) {
  // Simple markdown-to-HTML for council memos
  return content
    .split('\n')
    .map((line) => {
      // Headers
      if (line.startsWith('# ')) return `<h2 style="margin-top:1.5rem;margin-bottom:0.5rem;font-size:1.25rem">${line.slice(2)}</h2>`
      if (line.startsWith('## ')) return `<h3 style="margin-top:1.25rem;margin-bottom:0.5rem;font-size:1.1rem;color:var(--accent)">${line.slice(3)}</h3>`
      if (line.startsWith('### ')) return `<h4 style="margin-top:1rem;margin-bottom:0.25rem;font-size:0.95rem">${line.slice(4)}</h4>`
      // Bold lead/models line
      if (line.startsWith('**Lead:')) return `<p style="color:var(--text-secondary);font-size:0.8rem;margin-bottom:1rem">${line.replace(/\*\*/g, '<strong>').replace(/<strong>/g, '<strong>').replace(/<\/strong>/g, '</strong>')}</p>`
      // List items
      if (line.startsWith('- ')) return `<li style="margin-left:1rem;margin-bottom:0.25rem;color:var(--text-secondary);font-size:0.9rem">${line.slice(2).replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--text-primary)">$1</strong>')}</li>`
      if (/^\d+\.\s/.test(line)) return `<li style="margin-left:1rem;margin-bottom:0.25rem;color:var(--text-secondary);font-size:0.9rem">${line.replace(/^\d+\.\s/, '').replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--text-primary)">$1</strong>')}</li>`
      // Horizontal rule
      if (line.startsWith('---')) return '<hr style="border-color:var(--border);margin:1rem 0">'
      // Bold text in paragraphs
      if (line.trim()) return `<p style="color:var(--text-secondary);font-size:0.9rem;margin-bottom:0.5rem;line-height:1.6">${line.replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--text-primary)">$1</strong>')}</p>`
      return ''
    })
    .join('')
}

export default function Council() {
  const { data, isLoading } = useCouncilMemos()
  const { data: directives } = useCouncilDirectives()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const pendingActions = directives?.consensus_actions?.filter((a: any) => a.status === 'pending') || []
  const completedActions = directives?.consensus_actions?.filter((a: any) => a.status === 'complete') || []
  const killList = directives?.kill_list || []

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">R&D Council</h1>
        <p className="page-subtitle">
          Five AI models debate strategy twice daily. Each memo captures proposals, reactions, dissent, and consensus actions.
        </p>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        border: '1px solid #8b5cf6',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '2rem',
        fontSize: '0.875rem',
        color: 'var(--text-secondary)',
        lineHeight: '1.6',
      }}>
        <strong style={{ color: 'var(--text-primary)' }}>5 Models</strong>: Claude Opus 4.6 (Growth), GPT-5.4 (Architect), Gemini 3.1 Pro (Market), Claude Sonnet (Devil's Advocate), Claude Sonnet (Audience Proxy).
        {' '}Sessions run at <strong style={{ color: 'var(--text-primary)' }}>9am</strong> and <strong style={{ color: 'var(--text-primary)' }}>5pm</strong> daily. One model leads each day on rotation.
      </div>

      {(pendingActions.length > 0 || killList.length > 0) && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-header">
            <span className="card-title">Active Directives</span>
          </div>
          {pendingActions.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent)', marginBottom: '0.5rem' }}>
                PENDING ACTIONS ({pendingActions.length})
              </div>
              {pendingActions.map((a: any) => (
                <div key={a.id} style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', padding: '0.25rem 0', borderBottom: '1px solid var(--border)' }}>
                  <span className="badge badge-yellow" style={{ marginRight: '0.5rem', fontSize: '0.65rem' }}>PENDING</span>
                  {a.action}
                </div>
              ))}
            </div>
          )}
          {completedActions.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#22c55e', marginBottom: '0.5rem' }}>
                COMPLETED ({completedActions.length})
              </div>
              {completedActions.slice(0, 5).map((a: any) => (
                <div key={a.id} style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', padding: '0.25rem 0' }}>
                  <span className="badge badge-green" style={{ marginRight: '0.5rem', fontSize: '0.65rem' }}>DONE</span>
                  {a.action} {a.result && <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}> — {a.result}</span>}
                </div>
              ))}
            </div>
          )}
          {killList.length > 0 && (
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#ef4444', marginBottom: '0.5rem' }}>
                KILL LIST ({killList.length})
              </div>
              {killList.map((k: any) => (
                <div key={k.id || k.item} style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', padding: '0.25rem 0' }}>
                  <span className="badge badge-red" style={{ marginRight: '0.5rem', fontSize: '0.65rem' }}>KILLED</span>
                  {k.item}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : data?.memos?.length ? (
        <div>
          {data.memos.map((memo: any) => {
            const isExpanded = expandedId === memo.id

            return (
              <div
                key={memo.id}
                className="card"
                style={{ marginBottom: '1rem', cursor: 'pointer' }}
                onClick={() => setExpandedId(isExpanded ? null : memo.id)}
              >
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <span className={`badge ${sessionColors[memo.session] || 'badge-gray'}`}>
                    {memo.session.toUpperCase()}
                  </span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    {memo.date}
                  </span>
                  <span style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '0.7rem',
                    color: 'var(--text-secondary)',
                  }}>
                    Lead: {memo.lead}
                  </span>
                </div>

                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', lineHeight: '1.4' }}>
                  {memo.title}
                </h3>

                {!isExpanded && (
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem',
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical' as any,
                  }}>
                    {memo.content.replace(/^#.*$/gm, '').replace(/\*\*/g, '').replace(/---/g, '').trim().slice(0, 300)}...
                  </p>
                )}

                {isExpanded && (
                  <div
                    style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(memo.content) }}
                  />
                )}

                <div style={{
                  marginTop: '0.75rem',
                  fontSize: '0.75rem',
                  color: 'var(--accent)',
                }}>
                  {isExpanded ? 'Click to collapse' : 'Click to read full memo'}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="card">
          <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>
            No council memos yet. First session runs at 9am.
          </p>
        </div>
      )}
    </div>
  )
}
